import { Eye, EyeOff, GitCompareArrows } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';

import {
  type ConflictField,
  type ConflictLine,
  conflictFields,
  conflictLines,
  kindLabel,
  recordNames,
  recordTitle,
} from '../../shared/conflict';
import type { FocusSession } from '../../shared/focus';
import type { LocalRecord } from '../../shared/model';

import { workspace } from '../lib/engine';
import { StatusBadge } from './StatusBadge';
import { Button, IconButton, Modal } from './ui';

/** Records edited here and on another device, most recently edited first. */
export function conflictRecords(records: LocalRecord[]) {
  return records
    .filter(record => record.competing || record.clash)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

/** Whether a task, or the session running on it, is waiting for a choice. */
export function taskConflict(records: LocalRecord[], taskId: string) {
  return conflictRecords(records).find(
    record =>
      record.id === taskId ||
      (record.kind === 'focus' && (record.value as FocusSession).taskId === taskId) ||
      (record.clash?.kind === 'focus' && (record.clash.value as FocusSession).taskId === taskId),
  );
}

/** Asks the shell to open the comparison, from anywhere in the workspace. */
export function openConflictReview(id?: string) {
  window.dispatchEvent(new CustomEvent('cairn:conflict-review', { detail: id }));
}

/** The notice a held task or running session carries in place. */
export function TaskConflictNotice({
  records,
  taskId,
}: {
  records: LocalRecord[];
  taskId: string;
}) {
  const record = taskConflict(records, taskId);

  if (!record) {
    return null;
  }

  return (
    <div className="notice conflict-notice" role="status">
      <p>
        <strong>
          {record.clash
            ? 'A session is already running on another device.'
            : record.kind === 'focus'
              ? 'This session was also changed on another device.'
              : 'This task was also changed on another device.'}
        </strong>{' '}
        {record.clash
          ? 'Only one can run at a time. Your session here is kept on this device until you choose which one continues.'
          : 'Your edits are saved here and wait until you choose which version to keep.'}
      </p>
      <Button variant="secondary" onClick={() => openConflictReview(record.id)}>
        Compare versions
      </Button>
    </div>
  );
}

/**
 * The persistent notice under the top bar while any record waits for the
 * owner to choose a version. It cannot be dismissed: the choice is the only
 * thing that clears it, and the edit stays off the server until then.
 */
export function ConflictBanner({
  records,
  onReview,
}: {
  records: LocalRecord[];
  onReview: () => void;
}) {
  const waiting = conflictRecords(records);

  if (!waiting.length) {
    return null;
  }

  const first = waiting[0];
  const names = recordNames(records);

  return (
    <section className="conflict-banner" aria-label="Versions to review">
      <span className="conflict-banner-mark tone-danger" aria-hidden="true">
        <GitCompareArrows size={18} />
      </span>
      <div className="conflict-banner-text">
        <strong>
          {waiting.length === 1
            ? first.clash
              ? `A session is already running on another device`
              : `“${recordTitle(first.kind, first.value, names)}” was also changed on another device`
            : `${waiting.length} records were also changed on another device`}
        </strong>
        <p>
          Your {waiting.length === 1 ? 'edit is' : 'edits are'} saved on this device and nothing has
          been overwritten. Compare the two versions and choose the one to keep.
        </p>
      </div>
      <Button variant="secondary" onClick={onReview}>
        Compare versions
      </Button>
    </section>
  );
}

/**
 * Side-by-side review of every record waiting for a choice. Each record shows
 * only the properties that read differently, long text as a line comparison,
 * and one action per version directly beneath that version's heading.
 */
export function ConflictReview({
  records,
  initial,
  onClose,
}: {
  records: LocalRecord[];
  initial?: string;
  onClose: () => void;
}) {
  const waiting = conflictRecords(records);
  const names = useMemo(() => recordNames(records), [records]);

  const [selected, setSelected] = useState(initial),
    [busy, setBusy] = useState<'local' | 'remote'>(),
    [error, setError] = useState(''),
    [settled, setSettled] = useState('');

  const current = waiting.find(record => record.id === selected) ?? waiting[0];
  const title = (record: LocalRecord) => recordTitle(record.kind, record.value, names);

  useEffect(() => {
    if (!waiting.length && !busy) {
      onClose();
    }
  }, [waiting.length, busy, onClose]);

  const competing = current?.competing ?? current?.clash;

  if (!current || !competing) {
    return null;
  }

  // Two sessions at once, rather than two versions of one record.
  const clash = Boolean(current.clash);
  const heading = title(current);
  const fields = conflictFields(current.kind, current.value, competing.value, names);
  const otherTitle = recordTitle(competing.kind, competing.value, names);

  async function choose(keep: 'local' | 'remote') {
    const remaining = waiting.filter(record => record.id !== current.id);

    setBusy(keep);
    setError('');

    try {
      await workspace.resolveConflict(current.id, keep);

      // Named by the version that was kept, which may carry another title.
      setSettled(
        clash
          ? keep === 'local'
            ? `Kept the session started here; the other device's session was ended for review.`
            : `Kept the session running on the other device.`
          : keep === 'local'
            ? `Kept this device's version of “${heading}”.`
            : `Kept the other device's version of “${otherTitle}”.`,
      );

      setSelected(remaining[0]?.id);
    } catch (e) {
      setError(`Could not save your choice: ${(e as Error).message}`);
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      busy={Boolean(busy)}
      className="conflict-review"
      eyebrow={
        waiting.length === 1 ? 'ONE RECORD TO REVIEW' : `${waiting.length} RECORDS TO REVIEW`
      }
      title="Choose which version to keep"
      closeLabel="Close and decide later"
    >
      <p className="conflict-intro">
        These were edited on this device and on another one before they could sync. Nothing has been
        overwritten: your edit waits here until you choose.
      </p>
      {settled && (
        <p className="conflict-settled" role="status">
          {settled}
        </p>
      )}
      <div className={waiting.length > 1 ? 'conflict-layout' : 'conflict-layout single'}>
        {waiting.length > 1 && (
          <nav className="conflict-list" aria-label="Records to review">
            {waiting.map(record => (
              <button
                key={record.id}
                type="button"
                data-ui="specialized"
                className={
                  record.id === current.id
                    ? 'record-card conflict-item selected'
                    : 'record-card conflict-item'
                }
                aria-current={record.id === current.id ? 'true' : undefined}
                disabled={Boolean(busy)}
                onClick={() => {
                  setSelected(record.id);
                  setError('');
                }}
              >
                <span className="conflict-item-kind">{kindLabel(record.kind, record.value)}</span>
                <strong>{title(record)}</strong>
                <span className="small-muted">Edited {when(record.updatedAt)}</span>
              </button>
            ))}
          </nav>
        )}
        <section className="conflict-detail" aria-label={`Versions of ${heading}`}>
          <header className="conflict-heading">
            <StatusBadge tone="neutral">{kindLabel(current.kind, current.value)}</StatusBadge>
            <h3>{heading}</h3>
            {clash && (
              <p className="conflict-clash">
                A session was started here while another was already running on another device. Only
                one can run at a time, so this one has not been uploaded yet.
              </p>
            )}
          </header>
          <div className="conflict-versions">
            <VersionCard
              side="local"
              heading="This device"
              detail={
                clash
                  ? `Session started ${when(current.updatedAt)} · not uploaded yet`
                  : `Edited ${when(current.updatedAt)} · not uploaded yet`
              }
              action={clash ? 'Keep this session' : 'Keep this version'}
              busy={busy}
              onChoose={() => void choose('local')}
            />
            <VersionCard
              side="remote"
              heading="Other device"
              detail={
                clash
                  ? `Session running · saved ${when(competing.updatedAt)}`
                  : `Saved ${when(competing.updatedAt)} · on the server`
              }
              action={clash ? 'Keep that session' : 'Use this version'}
              busy={busy}
              onChoose={() => void choose('remote')}
            />
          </div>
          {error && (
            <p className="error-message" role="alert">
              {error}
            </p>
          )}
          {fields.length ? (
            // Keyed by record so a revealed secret or an opened fold stays with its record.
            <div className="conflict-fields" key={current.id}>
              <p className="conflict-count">
                {fields.length === 1 ? 'One difference' : `${fields.length} differences`}
              </p>
              {fields.map(field =>
                field.format === 'long' ? (
                  <LongField key={field.key} field={field} />
                ) : (
                  <ShortField key={field.key} field={field} />
                ),
              )}
            </div>
          ) : (
            <p className="conflict-same">
              The two versions read the same. They differ only in details Cairn does not show, such
              as when each was saved, so either choice keeps the same content.
            </p>
          )}
          {clash ? (
            <p className="conflict-consequence">
              <strong>Keep this session</strong> ends the other device's session and leaves its open
              time unconfirmed for you to review. <strong>Keep that session</strong> discards the
              session started here; the task stays as it was.
            </p>
          ) : (
            <p className="conflict-consequence">
              <strong>Keep this version</strong> uploads your edit; the other device's version stays
              in revision history. <strong>Use this version</strong> discards the edit made on this
              device.
            </p>
          )}
        </section>
      </div>
    </Modal>
  );
}

function VersionCard({
  side,
  heading,
  detail,
  action,
  busy,
  onChoose,
}: {
  side: 'local' | 'remote';
  heading: string;
  detail: string;
  action: string;
  busy?: 'local' | 'remote';
  onChoose: () => void;
}) {
  return (
    <article className={`conflict-version ${side}`}>
      <div>
        <span className="conflict-version-name">
          <span className="conflict-swatch" aria-hidden="true" />
          {heading}
        </span>
        <span className="small-muted">{detail}</span>
      </div>
      <Button variant="secondary" disabled={Boolean(busy)} onClick={onChoose}>
        {busy === side ? 'Saving…' : action}
      </Button>
    </article>
  );
}

function ShortField({ field }: { field: ConflictField }) {
  const [revealed, setRevealed] = useState(false);
  const secret = field.format === 'secret' && !revealed;

  return (
    <div className="conflict-field">
      <div className="conflict-field-label">
        <span>{field.label}</span>
        {field.format === 'secret' && (
          <IconButton
            size="compact"
            label={
              revealed ? `Hide ${field.label.toLowerCase()}` : `Show ${field.label.toLowerCase()}`
            }
            onClick={() => setRevealed(!revealed)}
          >
            {revealed ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
          </IconButton>
        )}
      </div>
      <div className="conflict-pair">
        <FieldValue side="local" value={field.local} masked={secret} />
        <FieldValue side="remote" value={field.remote} masked={secret} />
      </div>
    </div>
  );
}

function FieldValue({
  side,
  value,
  masked,
}: {
  side: 'local' | 'remote';
  value: string;
  masked: boolean;
}) {
  return (
    <div className={`conflict-value ${side}`}>
      <span className="conflict-value-side">
        {side === 'local' ? 'This device' : 'Other device'}
      </span>
      {value ? (
        <span className="conflict-value-text">{masked ? '••••••••' : value}</span>
      ) : (
        <span className="conflict-value-text empty">None</span>
      )}
    </div>
  );
}

/** Unchanged lines kept around each change; longer unchanged runs fold away. */
const context = 2;

function LongField({ field }: { field: ConflictField }) {
  const lines = useMemo(() => conflictLines(field.local, field.remote), [field]);
  const [opened, setOpened] = useState<number[]>([]);
  const labelId = useId();

  const pieces = fold(lines);

  const localCount = lines.filter(line => line.side === 'local').length,
    remoteCount = lines.filter(line => line.side === 'remote').length;

  return (
    <div className="conflict-field long">
      <div className="conflict-field-label">
        <span id={labelId}>{field.label}</span>
        <span className="conflict-legend">
          <span className="local">
            {localCount} {localCount === 1 ? 'line' : 'lines'} only here
          </span>
          <span className="remote">
            {remoteCount} {remoteCount === 1 ? 'line' : 'lines'} only on the other device
          </span>
        </span>
      </div>
      <ol className="conflict-lines" aria-labelledby={labelId}>
        {pieces.map(piece =>
          piece.type === 'gap' ? (
            <li key={`gap-${piece.start}`} className="conflict-gap">
              <Button variant="ghost" onClick={() => setOpened([...opened, piece.start])}>
                Show {piece.count} unchanged {piece.count === 1 ? 'line' : 'lines'}
              </Button>
            </li>
          ) : (
            <li key={`line-${piece.at}`} className={`conflict-line ${piece.line.side}`}>
              <span className="conflict-gutter">
                {/* A blank line between blocks keeps its tint but not a label. */}
                {!piece.line.text
                  ? ''
                  : piece.line.side === 'local'
                    ? 'Here'
                    : piece.line.side === 'remote'
                      ? 'Other'
                      : ''}
              </span>
              <span className="conflict-line-text">{piece.line.text || ' '}</span>
            </li>
          ),
        )}
      </ol>
    </div>
  );

  /** Lines to show, with unchanged runs longer than the context folded into a gap. */
  function fold(all: ConflictLine[]) {
    const near = all.map((_, index) =>
      all
        .slice(Math.max(0, index - context), index + context + 1)
        .some(line => line.side !== 'both'),
    );

    // Lines repeat, so their position in the comparison is their identity.
    const result: (
      | { type: 'line'; line: ConflictLine; at: number }
      | { type: 'gap'; start: number; count: number }
    )[] = [];

    let index = 0;

    while (index < all.length) {
      if (near[index]) {
        result.push({ type: 'line', line: all[index], at: index });
        index++;

        continue;
      }

      let end = index;

      while (end < all.length && !near[end]) {
        end++;
      }

      // Folding fewer than four lines saves nothing worth a click.
      if (end - index < 4 || opened.includes(index)) {
        for (let at = index; at < end; at++) {
          result.push({ type: 'line', line: all[at], at });
        }
      } else {
        result.push({ type: 'gap', start: index, count: end - index });
      }

      index = end;
    }

    return result;
  }
}

/** A save time as the owner would say it: "just now", "12 min ago", "today at 14:05". */
function when(iso: string) {
  const date = new Date(iso),
    minutes = Math.round((Date.now() - date.getTime()) / 60000);

  if (!Number.isFinite(minutes)) {
    return 'at an unknown time';
  }

  if (minutes < 1) {
    return 'just now';
  }

  if (minutes < 60) {
    return `${minutes} min ago`;
  }

  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (date.toDateString() === new Date().toDateString()) {
    return `today at ${time}`;
  }

  return `${date.toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${time}`;
}
